import React, { useState } from "react";
import AddressLocationSelector from "../addressLocation/AddressLocationSelector";
import { toLocationValue, fromLocationValue, countryCodeOf, STRIPE_FIELDS } from "../addressLocation/addressLocationData";
import { useStripe, useElements, CardNumberElement } from "@stripe/react-stripe-js";
import { FaLock, FaMapMarkerAlt, FaPlus, FaSpinner } from "react-icons/fa";
import CardSection from "./CardSection";
import { AttachPaymentMethod } from "../../auth/stripe";
import {
  isValidFullName,
  normalizePaymentMethodError,
  validateBillingAddress,
} from "./paymentMethodErrors";

const EMPTY_ADDRESS = { city: "", country: "", line1: "", postal_code: "", state: "" };

export default function CardSetupForm({
  infoSource,
  address,
  token,
  getAddressById,
  setAddCards,
  Customercreate,
  clearAddCardFromURL,
  setupIntentClientSecret,
  onSuccess,
  onCancel,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [name, setName] = useState("");
  const [addressA, setAddressA] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [billingAddress, setBillingAddress] = useState(EMPTY_ADDRESS);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cardComplete, setCardComplete] = useState({ number: false, expiry: false, cvc: false });
  const [confirmedPaymentMethodId, setConfirmedPaymentMethodId] = useState("");
  const [nameError, setNameError] = useState("");
  const [billingError, setBillingError] = useState("");
  const [cardErrors, setCardErrors] = useState({ number: "", expiry: "", cvc: "" });
  const [generalError, setGeneralError] = useState("");

  const text = (key, fallback) =>
    infoSource?.laguangeElakay?.("frontElakayHardcodedText", infoSource.languageAPI)?.cardSetupForm?.[key] || fallback;

  const showPaymentError = (error) => {
    const normalized = normalizePaymentMethodError(error, text);
    console.error("Unable to add payment method:", error);
    if (["number", "expiry", "cvc"].includes(normalized.field)) {
      setGeneralError("");
      setCardErrors((current) => ({ ...current, [normalized.field]: normalized.message }));
      return;
    }
    setGeneralError(normalized.message);
  };

  const selectAddress = async (event) => {
    const addressId = event.target.value;
    setSelectedAddressId(addressId);
    setBillingError("");
    if (!addressId) {
      setBillingAddress(EMPTY_ADDRESS);
      return;
    }

    try {
      const data = await getAddressById(token, addressId);
      if (data?.error || !data?.[0]) {
        throw new Error(data?.error || text("billingLoadError", "Unable to load this billing address."));
      }
      setBillingAddress({
        city: data[0].city || "",
        // Stripe wants the ISO code ("US"), older addresses stored "Us".
        country: countryCodeOf(data[0].country) || data[0].country || "",
        line1: data[0].Address1 || "",
        postal_code: data[0].postalCode || "",
        state: data[0].State || "",
      });
    } catch (error) {
      console.error("Unable to load billing address:", error);
      setBillingAddress(EMPTY_ADDRESS);
      setBillingError(error.message || text("billingLoadError", "Unable to load this billing address."));
    }
  };

  const handleCardChange = (field, event) => {
    setCardComplete((current) => ({ ...current, [field]: Boolean(event.complete) }));
    setCardErrors((current) => ({
      ...current,
      [field]: event.error ? normalizePaymentMethodError(event.error, text).message : "",
    }));
  };

  const validate = () => {
    let valid = true;
    const trimmedName = name.trim();

    if (!trimmedName) {
      setNameError(text("fullNameRequired", "Name on card is required."));
      valid = false;
    } else if (!isValidFullName(trimmedName)) {
      setNameError(text("fullNameInvalid", "Please enter a valid name on card."));
      valid = false;
    }

    if (!addressA && !selectedAddressId) {
      setBillingError(text("billingRequired", "Billing address is required."));
      valid = false;
    } else {
      const nextBillingError = validateBillingAddress(billingAddress, text);
      setBillingError(nextBillingError);
      if (nextBillingError) valid = false;
    }

    const incompleteMessages = {
      number: text("incompleteCardNumber", "Please complete your card number."),
      expiry: text("incompleteExpiry", "Your card's expiration date is incomplete."),
      cvc: text("incompleteCvc", "Your card's security code is incomplete."),
    };
    const nextCardErrors = { ...cardErrors };
    Object.keys(cardComplete).forEach((field) => {
      if (!cardComplete[field]) {
        nextCardErrors[field] = nextCardErrors[field] || incompleteMessages[field];
        valid = false;
      }
    });
    setCardErrors(nextCardErrors);
    return valid;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    setNameError("");
    setBillingError("");
    setGeneralError("");
    if (!validate()) return;

    if (!stripe || !elements) {
      setGeneralError(text("stripeNotReady", "The secure card form is still loading. Please try again."));
      return;
    }

    const card = elements.getElement(CardNumberElement);
    if (!card) {
      setGeneralError(text("stripeNotReady", "The secure card form is still loading. Please try again."));
      return;
    }

    setIsSubmitting(true);
    try {
      let paymentMethodId = confirmedPaymentMethodId;

      if (!paymentMethodId && setupIntentClientSecret) {
        const result = await stripe.confirmCardSetup(setupIntentClientSecret, {
          payment_method: {
            card,
            billing_details: { name: name.trim(), address: billingAddress },
          },
        });
        if (result.error) throw result.error;
        if (!result.setupIntent || result.setupIntent.status !== "succeeded" || !result.setupIntent.payment_method) {
          throw Object.assign(new Error(
            result.setupIntent?.last_setup_error?.message || text("authenticationFailed", "Card authentication failed. Please try again.")
          ), {
            type: result.setupIntent?.last_setup_error?.type || "setup_intent_error",
            code: result.setupIntent?.last_setup_error?.code || "setup_intent_authentication_failure",
          });
        }
        paymentMethodId = typeof result.setupIntent.payment_method === "string"
          ? result.setupIntent.payment_method
          : result.setupIntent.payment_method.id;
        setConfirmedPaymentMethodId(paymentMethodId);
      } else if (!paymentMethodId) {
        const result = await stripe.createPaymentMethod({
          type: "card",
          card,
          billing_details: { name: name.trim(), address: billingAddress },
        });
        if (result.error) throw result.error;
        if (!result.paymentMethod?.id) {
          throw new Error(text("unexpectedError", "Unable to add this payment method. Please try again."));
        }

        paymentMethodId = result.paymentMethod.id;
        const attached = await AttachPaymentMethod(token, { PM_id: paymentMethodId });
        if (!attached?.success) throw attached;
        setConfirmedPaymentMethodId(paymentMethodId);
      }

      if (!paymentMethodId) {
        throw new Error(text("unexpectedError", "Unable to add this payment method. Please try again."));
      }

      const refreshed = await Customercreate(token, { showGlobalError: false });
      const isVisible = refreshed?.paymentMethods?.some((method) => method.id === paymentMethodId);
      if (refreshed?.error || refreshed?.success === false || !isVisible) {
        throw refreshed?.error
          ? refreshed
          : new Error(text("backendConfirmationError", "The card was confirmed but could not be verified in your wallet. Please try again."));
      }

      onSuccess?.(text("cardAdded", "Card successfully added."));
      clearAddCardFromURL();
      setAddCards(false);
    } catch (error) {
      showPaymentError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddressChange = (field) => (event) => {
    setBillingAddress((current) => ({ ...current, [field]: event.target.value }));
    setBillingError("");
  };

  const selectedAddressData = address?.find((item) => item._id === selectedAddressId);
  const selectedAddressName = [selectedAddressData?.firstName, selectedAddressData?.lastName].filter(Boolean).join(" ");

  const selectedAddress = () => (
    <div className={`wallet-payment-field${billingError ? " wallet-payment-field--error" : ""}`}>
      <label className="wallet-payment-field__label" htmlFor="billing-address-select">
        {text("billingAddress", "Billing address")}
      </label>
      <select
        id="billing-address-select"
        value={selectedAddressId}
        onChange={selectAddress}
        className="wallet-payment-field__control"
        aria-invalid={Boolean(billingError)}
        aria-describedby={billingError ? "billing-address-error" : undefined}
      >
        <option value="">{infoSource.laguangeElakay("label", infoSource.languageAPI).pleaseSelect}</option>
        {address?.map((item) => (
          <option key={item._id} value={item._id}>
            ({item.firstName} {item.lastName}) {item.Address1}, {item.city} {item.State} {item.postalCode} {item.country}
          </option>
        ))}
      </select>
      <div id="billing-address-error" className="wallet-payment-field__message" aria-live="polite">
        {billingError && <span role="alert">{billingError}</span>}
      </div>
      {selectedAddressId && !billingError && (
        <div className="wallet-payment-address-summary">
          <FaMapMarkerAlt aria-hidden="true" />
          <div>
            <strong>{selectedAddressName || name || text("cardholder", "Cardholder")}</strong>
            <span>{billingAddress.line1}</span>
            <span>{[billingAddress.city, billingAddress.state, billingAddress.postal_code].filter(Boolean).join(", ")}</span>
            <span>{billingAddress.country}</span>
          </div>
        </div>
      )}
      <button
        onClick={() => {
          setAddressA(true);
          setSelectedAddressId("");
          setBillingAddress(EMPTY_ADDRESS);
          setBillingError("");
        }}
        type="button"
        className="wallet-payment-address-action"
      >
        <FaPlus aria-hidden="true" /> {text("addAnotherAddress", "Add another billing address")}
      </button>
    </div>
  );

  const manualAddress = () => (
    <div className="wallet-payment-address-grid">
      <div className="wallet-payment-field wallet-payment-address-grid__wide">
        <label className="wallet-payment-field__label" htmlFor="address-line1">{infoSource.laguangeElakay("label", infoSource.languageAPI).Address1}</label>
        <input id="address-line1" value={billingAddress.line1} onChange={handleAddressChange("line1")} type="text" autoComplete="billing address-line1" className="wallet-payment-field__control" />
      </div>
      <AddressLocationSelector
        idPrefix="billing"
        value={toLocationValue(billingAddress, STRIPE_FIELDS)}
        onChange={(location) => {
          setBillingError("");
          setBillingAddress((current) => fromLocationValue(current, location, STRIPE_FIELDS));
        }}
        emptyCountryLabel={infoSource.laguangeElakay("label", infoSource.languageAPI).selCountry}
        labels={{ country: infoSource.laguangeElakay("label", infoSource.languageAPI).country }}
        classNames={{ field: "wallet-payment-field", label: "wallet-payment-field__label", input: "wallet-payment-field__control", status: "wallet-payment-address-grid__wide" }}
      />
      <div className="wallet-payment-address-grid__wide wallet-payment-field__message" aria-live="polite">
        {billingError && <span role="alert">{billingError}</span>}
      </div>
      <button
        onClick={() => {
          setAddressA(false);
          setBillingAddress(EMPTY_ADDRESS);
          setBillingError("");
        }}
        type="button"
        className="wallet-payment-address-action wallet-payment-address-grid__wide"
      >
        {text("useSavedAddress", "Use a saved billing address")}
      </button>
    </div>
  );

  return (
    <form className="wallet-payment-form" onSubmit={handleSubmit} noValidate>
      <section className="wallet-payment-section" aria-labelledby="payment-information-title">
        <div className="wallet-payment-section__heading">
          <div>
            <h3 id="payment-information-title">{text("paymentInformation", "Payment information")}</h3>
            <p>{text("paymentInformationHint", "Enter your card information securely.")}</p>
          </div>
          <div className="wallet-payment-secure-chip"><FaLock aria-hidden="true" /> {text("secure", "Secure")}</div>
        </div>
        <div className={`wallet-payment-field${nameError ? " wallet-payment-field--error" : ""}`}>
          <label className="wallet-payment-field__label" htmlFor="full-name">{text("nameOnCard", "Name on card")}</label>
          <input
            value={name}
            onChange={(event) => { setName(event.target.value); setNameError(""); }}
            type="text"
            id="full-name"
            autoComplete="cc-name"
            className="wallet-payment-field__control"
            placeholder={text("nameExample", "full name ....yvox")}
            aria-invalid={Boolean(nameError)}
            aria-describedby={nameError ? "full-name-error" : undefined}
          />
          <div id="full-name-error" className="wallet-payment-field__message" aria-live="polite">{nameError && <span role="alert">{nameError}</span>}</div>
        </div>
        <CardSection text={text} errors={cardErrors} complete={cardComplete} onChange={handleCardChange} />
      </section>

      <section className="wallet-payment-section wallet-payment-billing" aria-labelledby="billing-information-title">
        <div className="wallet-payment-section__heading wallet-payment-section__heading--compact">
          <div>
            <h3 id="billing-information-title">{text("billingSection", "Billing address")}</h3>
            <p>{text("billingHint", "Choose the address associated with this card.")}</p>
          </div>
        </div>
        {addressA ? manualAddress() : selectedAddress()}
      </section>

      {generalError && (
        <div className="wallet-payment-general-error" role="alert">
          <strong aria-hidden="true">⚠</strong><span>{generalError}</span>
        </div>
      )}

      <div className="wallet-payment-actions">
        <button className="wallet-payment-actions__cancel" type="button" onClick={onCancel} disabled={isSubmitting}>
          {text("cancel", "Cancel")}
        </button>
        <button className="wallet-payment-actions__save" type="submit" disabled={!stripe || isSubmitting}>
          {isSubmitting && <FaSpinner className="wallet-payment-spinner" aria-hidden="true" />}
          {isSubmitting ? text("saving", "Saving...") : text("savePaymentMethod", "Save payment method")}
        </button>
      </div>

      <div className="wallet-payment-security"><FaLock aria-hidden="true" /> {text("stripeSecurity", "Your card information is securely processed by Stripe.")}</div>
    </form>
  );
}
